import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, Lock, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [passcode, setPasscode] = useState('');
  const [authed, setAuthed] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchContacts = async (code) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: { 'X-Admin-Passcode': code },
      });
      setSubmissions(res.data);
      setAuthed(true);
    } catch (err) {
      setError('Invalid passcode. Please try again.');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    fetchContacts(passcode);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--sage-bg, #f4f1ea)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(74, 124, 89, 0.1)' }}>
                <Lock className="w-7 h-7" style={{ color: 'var(--sage-primary, #4a7c59)' }} />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-center mb-2" style={{ color: 'var(--sage-dark)' }}>Admin Access</h1>
            <p className="text-gray-600 text-center text-sm mb-6">Enter the passcode to view contact inquiries.</p>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                className="form-input"
                placeholder="Passcode"
                data-testid="admin-passcode-input"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                required
              />
              {error && <p className="text-red-600 text-sm mt-2" data-testid="admin-error">{error}</p>}
              <Button
                type="submit"
                className="w-full text-white mt-4"
                style={{ backgroundColor: 'var(--sage-primary, #4a7c59)' }}
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Verifying...' : 'View Inquiries'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 py-10" style={{ backgroundColor: 'var(--sage-bg, #f4f1ea)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--sage-dark)' }}>Contact Inquiries</h1>
            <p className="text-gray-600 text-sm mt-1" data-testid="admin-count">{submissions.length} total submission(s)</p>
          </div>
          <Button
            variant="outline"
            onClick={() => fetchContacts(passcode)}
            disabled={loading}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {submissions.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">No inquiries yet.</CardContent></Card>
        ) : (
          <div className="space-y-4">
            {submissions.map((s) => (
              <Card key={s.id} data-testid={`admin-submission-${s.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="text-lg font-bold" style={{ color: 'var(--sage-dark)' }}>{s.name}</h3>
                    <span className="text-xs text-gray-500">{fmt(s.created_at)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm">
                    <a href={`mailto:${s.email}`} className="flex items-center gap-2 hover:underline" style={{ color: 'var(--earth-accent)' }}>
                      <Mail className="w-4 h-4" /> {s.email}
                    </a>
                    {s.phone && (
                      <a href={`tel:${s.phone.replace(/\D/g, '')}`} className="flex items-center gap-2 hover:underline" style={{ color: 'var(--earth-accent)' }}>
                        <Phone className="w-4 h-4" /> {s.phone}
                      </a>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.emailed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {s.emailed ? 'Emailed' : 'Not emailed'}
                    </span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap text-sm p-3 rounded" style={{ backgroundColor: 'rgba(139, 111, 71, 0.08)' }}>{s.message}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
